import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchAllActiveCompanies } from "../../db/Companies";
import CompanyCard from "./CompanyCard";
import PageHeader from "../../components/controls/PageHeader";
import AnanasSolutionsLogo from "../../images/AnanasSolutionsLogo.webp";

const PAGE_SIZE = 12;

function normalizeText(value) {
  return (value || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function matchesCompany(company, searchText, selectedRegionId) {
  const query = normalizeText(searchText);
  const name = normalizeText(company?.name);
  const address = normalizeText(company?.address);
  const regionId = company?.location?.regionId ?? null;

  const matchesText = !query || name.includes(query) || address.includes(query);
  const matchesRegion =
    !selectedRegionId || regionId === Number(selectedRegionId);

  return matchesText && matchesRegion;
}

function clampPage(page, totalPages) {
  if (totalPages < 1) return 1;
  if (page < 1) return 1;
  if (page > totalPages) return totalPages;
  return page;
}

function getPaginationItems(page, totalPages) {
  if (totalPages <= 7)
    return Array.from({ length: totalPages }, (_, i) => i + 1);

  const left = Math.max(2, page - 1);
  const right = Math.min(totalPages - 1, page + 1);
  const items = [1];

  if (left > 2) items.push("...");
  for (let current = left; current <= right; current++) items.push(current);
  if (right < totalPages - 1) items.push("...");
  items.push(totalPages);

  return items;
}

function FiltersBar({ t, searchText, setSearchText }) {
  return (
    <div className="mb-8 flex justify-center">
      <div className="w-full max-w-xl text-center">
        <div className="text-primary/90 text-md mb-2">
          Rerchercher par compagnie
        </div>
        <input
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder={t("storeFilters.aria.companySelect")}
          className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-primary text-sm shadow-sm outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/10"
        />
      </div>
    </div>
  );
}

function PaginationDaisy({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;

  const items = getPaginationItems(page, totalPages);
  const normalBtn =
    "join-item btn btn-sm bg-white border-gray-200 text-gray-700 hover:bg-gray-50";
  const activeBtn =
    "join-item btn btn-sm btn-active bg-primary text-white border-primary";
  const disabledBtn =
    "join-item btn btn-sm btn-disabled bg-white border-gray-200 text-gray-400";

  return (
    <div className="mt-10 flex justify-center">
      <div className="join shadow-sm">
        <button
          className={normalBtn}
          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          disabled={page === 1}
        >
          «
        </button>

        {items.map((item, index) =>
          item === "..." ? (
            <button key={`dots-${index}`} className={disabledBtn}>
              ...
            </button>
          ) : (
            <button
              key={item}
              className={item === page ? activeBtn : normalBtn}
              onClick={() => setPage(item)}
            >
              {item}
            </button>
          )
        )}

        <button
          className={normalBtn}
          onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
          disabled={page === totalPages}
        >
          »
        </button>
      </div>
    </div>
  );
}

export default function CompanyGallery() {
  const [companies, setCompanies] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedRegionId, setSelectedRegionId] = useState("");
  const [page, setPage] = useState(1);
  const { t } = useTranslation();

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const result = await fetchAllActiveCompanies();
        setCompanies(result || []);
      } catch (error) {
        console.error(error);
      }
    };
    loadCompanies();
  }, []);

  const activeCompanies = useMemo(
    () => companies.filter((company) => company.isActive),
    [companies]
  );

  const filteredCompanies = useMemo(
    () =>
      activeCompanies.filter((company) =>
        matchesCompany(company, searchText, selectedRegionId)
      ),
    [activeCompanies, searchText, selectedRegionId]
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredCompanies.length / PAGE_SIZE)),
    [filteredCompanies.length]
  );

  useEffect(() => {
    setPage(1);
  }, [searchText, selectedRegionId]);

  useEffect(() => {
    setPage((current) => clampPage(current, totalPages));
  }, [totalPages]);

  const pagedCompanies = useMemo(() => {
    const startIndex = (page - 1) * PAGE_SIZE;
    return filteredCompanies.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredCompanies, page]);

  return (
    <div className="min-h-screen bg-gray-50/50">
      <PageHeader
        title={t("partners.title")}
        subtitle={t("partners.description")}
        logoSrc={AnanasSolutionsLogo}
        align="center"
        fullBleed={true}
        rounded={false}
        height="sm"
        gradient={{ from: "#E9FBFA", via: "#FFF9E6", to: "#FFE9EC" }}
        patternSize="32px 32px"
        patternOpacity={15}
      />

      <div className="w-full">
        <div className="mx-auto w-full max-w-6xl px-6 py-10">
          <FiltersBar
            t={t}
            searchText={searchText}
            setSearchText={setSearchText}
          />

          <div className="grid gap-4 sm:gap-5 justify-center grid-cols-[repeat(auto-fill,22rem)]">
            {pagedCompanies.map((company) => (
              <CompanyCard key={company.id} company={company} />
            ))}
          </div>

          {filteredCompanies.length === 0 && (
            <div className="mt-10 text-center text-sm text-gray-500">
              {t("partners.noResults")}
            </div>
          )}

          <PaginationDaisy
            page={page}
            totalPages={totalPages}
            setPage={setPage}
          />
        </div>
      </div>
    </div>
  );
}
